/**
 * Retrait rapide mobile — Phase 101dc (Feb 2026).
 *
 * Interface mobile-first pour opérateurs :
 * 1. Choisir un distributeur (grands boutons)
 * 2. Choisir un produit → le lot FIFO (plus proche DLC) est pris automatiquement
 * 3. Motif + quantité → validation
 *
 * Réutilise l'endpoint existant POST /api/lots/{id}/withdraw qui alimente
 * automatiquement le rapport Retirées (Phase 101db) et le widget écart.
 * Pas d'écriture Nayax — 100% côté AM.
 */
import React, { useEffect, useMemo, useState } from "react";
import { api } from "../lib/api";
import { useAuth } from "../lib/auth";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Building2, Package, AlertTriangle, TrendingDown, CheckCircle2, Loader2, ArrowLeft, Minus, Plus, PackageMinus, Truck } from "lucide-react";

const REASONS = [
  // Phase 101dt : DLC_EXPIRED transite désormais par Stock Conthey (au lieu d'un OUT sec)
  { code: "DLC_EXPIRED", label: "DLC périmée", sublabel: "→ retour Conthey", icon: AlertTriangle, color: "bg-[#B93A25] hover:bg-[#8B2D1D]", tone: "danger", isTransfer: true },
  { code: "BROKEN", label: "Barquette foutue", sublabel: "sortie sèche", icon: PackageMinus, color: "bg-[#C77D3A] hover:bg-[#9C5F26]", tone: "warn" },
  { code: "RETURN_CONTHEY", label: "Retour Conthey", sublabel: "transfert direct", icon: Truck, color: "bg-[#8C6B2F] hover:bg-[#6F531F]", tone: "info", isTransfer: true },
  { code: "BLOCKED", label: "Bloquée machine", sublabel: "sortie sèche", icon: Minus, color: "bg-[#5B3A8C] hover:bg-[#3D2A5C]", tone: "purple" },
];

const fmtDLC = (iso) => {
  if (!iso) return "—";
  const d = new Date(iso);
  return d.toLocaleDateString("fr-CH", { day: "2-digit", month: "2-digit", year: "numeric" });
};

const dlcClass = (iso) => {
  if (!iso) return "text-[#575A57]";
  const days = Math.floor((new Date(iso) - Date.now()) / 86400000);
  if (days < 0) return "text-[#B93A25] font-semibold";
  if (days <= 2) return "text-[#C77D3A] font-semibold";
  if (days <= 5) return "text-[#8C6B2F]";
  return "text-[#575A57]";
};

export default function RetraitMobile() {
  const { user } = useAuth();
  const [step, setStep] = useState(1);
  const [distributors, setDistributors] = useState([]);
  const [distributor, setDistributor] = useState(null);
  const [lots, setLots] = useState([]);
  const [product, setProduct] = useState(null); // { productId, productName, lots[], fifoLot }
  const [loading, setLoading] = useState(false);
  const [qty, setQty] = useState(1);
  const [reason, setReason] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(null);
  const [error, setError] = useState(null);

  const [products, setProducts] = useState([]);
  const [allDistributors, setAllDistributors] = useState([]);

  const operatorName = (user && user !== false) ? (user.name || user.email || "") : "";

  // Phase 101dc — Injection dynamique du manifest mini-AM pour PWA installable
  // depuis cette page uniquement (n'écrase pas le manifest boutique).
  useEffect(() => {
    const existingLink = document.querySelector('link[rel="manifest"]');
    const existingHref = existingLink?.getAttribute("href");
    if (existingLink) existingLink.setAttribute("href", "/manifest-retrait.json");
    const existingTheme = document.querySelector('meta[name="theme-color"]');
    const existingThemeContent = existingTheme?.getAttribute("content");
    if (existingTheme) existingTheme.setAttribute("content", "#A04E2C");
    const previousTitle = document.title;
    document.title = "mini-AM · Retrait";
    return () => {
      if (existingLink && existingHref) existingLink.setAttribute("href", existingHref);
      if (existingTheme && existingThemeContent) existingTheme.setAttribute("content", existingThemeContent);
      document.title = previousTitle;
    };
  }, []);

  // Charger les distributeurs + produits
  useEffect(() => {
    setLoading(true);
    Promise.all([
      api.get("/distributors"),
      api.get("/products"),
    ]).then(([dr, pr]) => {
      const all = dr.data || [];
      setAllDistributors(all);
      setDistributors(all.filter((d) => d.type === "AUTOMATIQUE"));
      setProducts(pr.data || []);
    }).catch((e) => setError(e?.response?.data?.detail || "Erreur chargement"))
      .finally(() => setLoading(false));
  }, []);

  const productsById = useMemo(() => {
    const m = {};
    for (const p of products) m[p.id] = p;
    return m;
  }, [products]);

  // Charger les lots du distributeur sélectionné
  useEffect(() => {
    if (!distributor) return;
    setLoading(true);
    api.get(`/lots?distributor_id=${distributor.id}`).then((r) => {
      const active = (r.data || []).filter((l) => !l.archived && (l.quantity || 0) > 0);
      setLots(active);
    }).catch((e) => setError(e?.response?.data?.detail || "Erreur chargement lots")).finally(() => setLoading(false));
  }, [distributor]);

  // Agréger par produit (avec le lot FIFO plus proche DLC)
  const productsAtDist = useMemo(() => {
    const map = {};
    for (const l of lots) {
      const pid = l.product_id;
      const p = productsById[pid];
      if (!map[pid]) {
        map[pid] = {
          productId: pid,
          productName: p?.name || "(?)",
          productSku: p?.sku || "",
          lots: [],
          totalQty: 0,
        };
      }
      map[pid].lots.push(l);
      map[pid].totalQty += Number(l.quantity || 0);
    }
    // Tri des lots FIFO (DLC croissante)
    for (const p of Object.values(map)) {
      p.lots.sort((a, b) => (new Date(a.dlc || "9999-12-31")) - (new Date(b.dlc || "9999-12-31")));
      p.fifoLot = p.lots[0];
    }
    // Tri produits : DLC du FIFO ascendante (les plus urgents en tête)
    return Object.values(map).sort((a, b) => {
      const ad = new Date(a.fifoLot?.dlc || "9999-12-31");
      const bd = new Date(b.fifoLot?.dlc || "9999-12-31");
      return ad - bd;
    });
  }, [lots]);

  const submit = async () => {
    if (!product?.fifoLot || !reason) return;
    if (qty <= 0) return;
    setSubmitting(true);
    setError(null);
    try {
      const payload = {
        reason: reason.code,
        quantity: qty,
        notes: `Retrait rapide mobile · ${operatorName || "opérateur"}`,
      };
      // RETURN_CONTHEY = TRANSFER → destination_distributor_id requis
      if (reason.code === "RETURN_CONTHEY") {
        const conthey = allDistributors.find(
          (d) => (d.name || "").toLowerCase().includes("conthey")
        );
        if (!conthey) {
          throw new Error("Distributeur Conthey introuvable — impossible de faire le retour.");
        }
        payload.destination_distributor_id = conthey.id;
      }
      await api.post(`/lots/${product.fifoLot.id}/withdraw`, payload);
      setSuccess({
        productName: product.productName,
        lotNumber: product.fifoLot.lot_number,
        qty,
        reasonLabel: reason.label,
        reasonCode: reason.code,
        isTransfer: !!reason.isTransfer,
        distName: distributor.name,
      });
    } catch (e) {
      setError(e?.response?.data?.detail || e.message);
    } finally {
      setSubmitting(false);
    }
  };

  const resetAll = () => {
    setStep(1);
    setDistributor(null);
    setProduct(null);
    setReason(null);
    setQty(1);
    setSuccess(null);
    setError(null);
    setLots([]);
  };

  // ============== Écran succès ==============
  if (success) {
    return (
      <div className="min-h-screen bg-[#F5F5F0] p-4 flex items-center justify-center" data-testid="retrait-success">
        <div className="w-full max-w-md bg-white border border-[#E5E5E0] rounded-xl p-6 shadow-md text-center">
          <CheckCircle2 className="w-16 h-16 text-[#1F4E3D] mx-auto mb-3" />
          <h1 className="font-heading text-xl font-medium text-[#1A1C1A] mb-2">Retrait enregistré</h1>
          <div className="bg-[#F0F7F1] border border-[#C4DDCB] rounded p-3 text-left text-sm mb-4">
            <div><b>{success.qty}× {success.productName}</b></div>
            <div className="text-[#575A57] text-xs mt-0.5">Lot {success.lotNumber || "?"} · {success.distName}</div>
            <div className="text-[#575A57] text-xs mt-0.5">Motif : {success.reasonLabel}</div>
            {success.isTransfer && (
              <div className="text-[#1F4E3D] text-xs mt-1 font-semibold flex items-center gap-1">
                <Truck className="w-3 h-3" /> Transférées vers Stock Conthey
              </div>
            )}
          </div>
          <div className="flex gap-2">
            <Button onClick={resetAll} className="flex-1 bg-[#1F4E3D] hover:bg-[#173B2E] text-white" data-testid="retrait-new">
              Nouveau retrait
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F5F5F0] p-3 sm:p-6" data-testid="retrait-mobile-page">
      {/* Header */}
      <div className="max-w-2xl mx-auto mb-4">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-[#A04E2C] flex items-center justify-center text-white">
            <PackageMinus className="w-5 h-5" />
          </div>
          <div className="flex-1">
            <h1 className="font-heading text-xl font-medium text-[#1A1C1A]">Retrait rapide</h1>
            <div className="text-xs text-[#575A57]">
              {step === 1 && "Choisir le distributeur"}
              {step === 2 && distributor && <><b>{distributor.name}</b> · Choisir un plat</>}
              {step === 3 && product && <><b>{product.productName}</b> · Motif et quantité</>}
            </div>
          </div>
          {step > 1 && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => { if (step === 3) { setProduct(null); setStep(2); } else if (step === 2) { setDistributor(null); setStep(1); } }}
              data-testid="retrait-back"
            >
              <ArrowLeft className="w-4 h-4 mr-1" /> Retour
            </Button>
          )}
        </div>
      </div>

      {error && (
        <div className="max-w-2xl mx-auto mb-3 bg-[#FCEEEA] border border-[#C05C47]/30 text-[#8B3D2D] text-sm rounded-md px-3 py-2">
          {error}
        </div>
      )}

      {loading && (
        <div className="max-w-2xl mx-auto flex items-center justify-center py-12">
          <Loader2 className="w-6 h-6 animate-spin text-[#1F4E3D]" />
        </div>
      )}

      {/* Étape 1 : distributeurs */}
      {!loading && step === 1 && (
        <div className="max-w-2xl mx-auto space-y-2" data-testid="step-distributors">
          {distributors.map((d) => (
            <button
              key={d.id}
              type="button"
              onClick={() => { setDistributor(d); setStep(2); }}
              className="w-full text-left bg-white border border-[#E5E5E0] rounded-lg p-4 hover:border-[#1F4E3D] hover:shadow-md transition-all flex items-center gap-3"
              data-testid={`dist-btn-${d.id}`}
            >
              <div className="w-10 h-10 rounded-md bg-[#F5F5F0] flex items-center justify-center">
                <Building2 className="w-5 h-5 text-[#1F4E3D]" />
              </div>
              <div className="flex-1">
                <div className="font-medium text-[#1A1C1A]">{d.name}</div>
                <div className="text-xs text-[#575A57]">{d.city || "—"}</div>
              </div>
            </button>
          ))}
          {distributors.length === 0 && (
            <div className="text-center text-sm text-[#A3A6A3] italic py-8">Aucun distributeur automatique.</div>
          )}
        </div>
      )}

      {/* Étape 2 : produits présents */}
      {!loading && step === 2 && (
        <div className="max-w-2xl mx-auto space-y-2" data-testid="step-products">
          {productsAtDist.map((p) => (
            <button
              key={p.productId}
              type="button"
              onClick={() => { setProduct(p); setStep(3); }}
              className="w-full text-left bg-white border border-[#E5E5E0] rounded-lg p-3 hover:border-[#1F4E3D] hover:shadow-md transition-all flex items-center gap-3"
              data-testid={`prod-btn-${p.productId}`}
            >
              <div className="w-10 h-10 rounded-md bg-[#F5F5F0] flex items-center justify-center">
                <Package className="w-5 h-5 text-[#1F4E3D]" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="font-medium text-[#1A1C1A] truncate">{p.productName}</div>
                <div className="text-xs text-[#575A57] flex items-center gap-2 flex-wrap">
                  <span>Lot <b>{p.fifoLot?.lot_number || "?"}</b></span>
                  <span className={dlcClass(p.fifoLot?.dlc)}>DLC {fmtDLC(p.fifoLot?.dlc)}</span>
                  <span>· <b className="font-mono">{p.totalQty}</b> disp.</span>
                </div>
              </div>
            </button>
          ))}
          {productsAtDist.length === 0 && (
            <div className="text-center text-sm text-[#A3A6A3] italic py-8">Aucun lot présent sur ce distributeur.</div>
          )}
        </div>
      )}

      {/* Étape 3 : motif + quantité */}
      {step === 3 && product && (
        <div className="max-w-2xl mx-auto space-y-4" data-testid="step-reason">
          {/* Récap lot */}
          <div className="bg-white border border-[#E5E5E0] rounded-lg p-3">
            <div className="text-[10px] uppercase tracking-wider text-[#575A57] mb-1">Lot FIFO sélectionné</div>
            <div className="font-medium text-[#1A1C1A]">{product.productName}</div>
            <div className="text-xs text-[#575A57] mt-0.5">
              Lot <b>{product.fifoLot?.lot_number}</b> · DLC {fmtDLC(product.fifoLot?.dlc)} · Dispo : <b className="font-mono">{product.fifoLot?.quantity}</b>
            </div>
          </div>

          {/* Quantité */}
          <div className="bg-white border border-[#E5E5E0] rounded-lg p-4">
            <div className="text-[10px] uppercase tracking-wider text-[#575A57] mb-2">Quantité retirée</div>
            <div className="flex items-center justify-center gap-3">
              <Button
                variant="outline" size="lg"
                onClick={() => setQty((q) => Math.max(1, q - 1))}
                className="w-14 h-14 text-2xl"
                data-testid="qty-minus"
              >
                −
              </Button>
              <Input
                type="number" min="1"
                value={qty}
                onChange={(e) => setQty(Math.max(1, Number(e.target.value || 1)))}
                className="w-24 h-14 text-2xl text-center font-mono font-semibold"
                data-testid="qty-input"
              />
              <Button
                variant="outline" size="lg"
                onClick={() => setQty((q) => Math.min(product.fifoLot?.quantity || 999, q + 1))}
                className="w-14 h-14 text-2xl"
                data-testid="qty-plus"
              >
                +
              </Button>
            </div>
            {qty > (product.fifoLot?.quantity || 0) && (
              <div className="mt-2 text-xs text-[#B93A25] text-center">
                Attention — quantité supérieure au stock disponible
              </div>
            )}
          </div>

          {/* Motif — 4 gros boutons */}
          <div>
            <div className="text-[10px] uppercase tracking-wider text-[#575A57] mb-2 px-1">Motif du retrait</div>
            <div className="grid grid-cols-2 gap-2">
              {REASONS.map((r) => {
                const Icon = r.icon;
                const isSelected = reason?.code === r.code;
                return (
                  <button
                    key={r.code}
                    type="button"
                    onClick={() => setReason(r)}
                    className={`p-4 rounded-lg border-2 text-white transition-all flex flex-col items-center gap-1 ${r.color} ${isSelected ? "ring-4 ring-[#1F4E3D]/40 scale-[1.02]" : "border-transparent"}`}
                    data-testid={`reason-${r.code}`}
                  >
                    <Icon className="w-6 h-6" />
                    <div className="text-sm font-semibold text-center leading-tight">{r.label}</div>
                    {r.sublabel && <div className="text-[9px] opacity-90">{r.sublabel}</div>}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Valider */}
          <Button
            onClick={submit}
            disabled={!reason || qty <= 0 || submitting}
            className="w-full h-14 bg-[#1F4E3D] hover:bg-[#173B2E] text-white text-lg font-semibold"
            data-testid="retrait-submit"
          >
            {submitting ? (
              <><Loader2 className="w-5 h-5 mr-2 animate-spin" /> Enregistrement…</>
            ) : (
              <>Valider le retrait</>
            )}
          </Button>
        </div>
      )}
    </div>
  );
}
